/**
 * Centralized utility for referral link generation and formatting
 * Ensures consistent referral link format across the entire application
 */

/**
 * Get the base URL for referral links
 * Uses window.location.origin in browser, falls back to localhost for tests
 */
export function getReferralBaseUrl(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'http://localhost:8080';
}

/**
 * Generate a referral link from a referral code
 * @param referralCode - The referral code (will be sanitized)
 * @returns The complete referral link
 */
export function generateReferralLink(referralCode: string | null | undefined): string {
  if (!referralCode) {
    return '';
  }
  
  // Sanitize referral code - only allow alphanumeric characters
  const sanitizedCode = referralCode.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  
  if (!sanitizedCode) {
    return '';
  }
  
  const baseUrl = getReferralBaseUrl();
  return `${baseUrl}/ref/${sanitizedCode}`;
}

/**
 * Extract referral code from a referral link
 * @param link - The referral link
 * @returns The referral code or null if invalid
 */
export function extractReferralCodeFromLink(link: string): string | null {
  try {
    const url = new URL(link);
    const pathParts = url.pathname.split('/');
    const refIndex = pathParts.indexOf('ref');
    
    if (refIndex !== -1 && pathParts[refIndex + 1]) {
      const code = pathParts[refIndex + 1].trim().toUpperCase();
      // Validate: alphanumeric only
      if (/^[A-Z0-9]+$/.test(code)) {
        return code;
      }
    }
    
    return null;
  } catch {
    // If URL parsing fails, try simple regex
    const match = link.match(/\/ref\/([A-Z0-9]+)/i);
    if (match && match[1]) {
      return match[1].toUpperCase();
    }
    return null;
  }
}

/**
 * Validate referral code format
 * Referral codes should be alphanumeric, typically 8 characters
 * @param code - The referral code to validate
 * @returns true if valid, false otherwise
 */
export function isValidReferralCodeFormat(code: string | null | undefined): boolean {
  if (!code || typeof code !== 'string') {
    return false;
  }
  
  const trimmed = code.trim();
  
  // Check length (reasonable bounds)
  if (trimmed.length < 1 || trimmed.length > 20) {
    return false;
  }
  
  // Only alphanumeric characters allowed
  return /^[A-Z0-9]+$/i.test(trimmed);
}

